import type { AiClientInsightRunSummary } from "@closer/shared";
import {
  AI_CERTAINTY_WARNING,
  AI_CLIENT_DUPLICATE_UNVERIFIED_WARNING,
  AI_CLIENT_INSIGHT_ADVISORY,
  AI_CLIENT_INSIGHT_CATEGORY_NAMES,
  AI_CLIENT_INSIGHT_EMPTY_MESSAGE,
  AI_CLIENT_INSIGHT_PRIORITY_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * MÜVEKKİL/PORTFÖY AI İÇGÖRÜLERİ (CLOS-289 `[193]` · CLOS-1019).
 *
 * ### ⚠ "BİRLEŞTİR" DÜĞMESİ YOK
 *
 * Mükerrer içgörüsü en tehlikeli olanı: yanlış birleştirme geri alması en pahalı
 * işlemlerden biri. Ekran iddiayı gösteriyor ve DOĞRULANIP doğrulanmadığını söylüyor;
 * birleştirme kararı müvekkil ekranının kanonik akışında kalıyor.
 *
 * ### ⚠ DOĞRULANMAMIŞ İDDİA AYRICA UYARILIYOR
 *
 * Sunucu her mükerrer iddiasını kanonik eşleştiriciyle karşılaştırıyor. `false` gelen
 * iddia ekranda uyarı taşıyor — "AI öyle diyor" ile "sistem de aynı kaydı gösteriyor"
 * arasındaki fark, kullanıcının vereceği kararı doğrudan değiştiriyor.
 */
export function AiClientInsightSection() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const anahtar = tenantKey(tenantId, "muvekkil-ai-icgoruler");
  const liste = useQuery({
    queryKey: anahtar,
    enabled: can("client.view"),
    queryFn: async () =>
      (await api<AiClientInsightRunSummary[]>(`/api/clients/ai-insights`, { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<AiClientInsightRunSummary>(`/api/clients/ai-insights`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      /** ⚠ Sonuç doğrudan önbelleğe (CLOS-288'in dersi: liste tazelemesinin yarışına bırakma). */
      queryClient.setQueryData<AiClientInsightRunSummary[]>(anahtar, (eski) => [
        sonuc,
        ...(eski ?? []),
      ]);
      await queryClient.invalidateQueries({ queryKey: anahtar });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İçgörüler üretilemedi"),
  });

  if (!can("client.view")) return null;

  const son = (liste.data ?? []).find((c) => c.status === "completed") ?? null;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">AI portföy içgörüleri</h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uret.isPending}
          disabled={uret.isPending}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? "İnceleniyor…" : son ? "Yeniden incele" : "Portföyümü incele"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-muvekkil-uyari">
        {AI_CLIENT_INSIGHT_ADVISORY}
      </p>

      <div className="mt-4">
        {liste.isPending ? (
          <Skeleton lines={3} />
        ) : liste.error ? (
          <StateView
            state={resolveErrorState(liste.error, {
              idempotent: true,
              overrides: {
                error: { title: "İçgörüler yüklenemedi", description: "Bölüm getirilemedi." },
              },
            })}
            onRetry={() => void liste.refetch()}
          />
        ) : !son ? (
          <p className="text-sm text-fg-muted">
            Müvekkil kayıtlarınızı ve portföyünüzü AI ile inceleyip içgörü alabilirsiniz.
          </p>
        ) : son.insights.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="ai-muvekkil-bos">
            {AI_CLIENT_INSIGHT_EMPTY_MESSAGE}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge tone="neutral">{son.model}</Badge>
              <span className="tabular-nums">{son.credits} kredi</span>
            </div>

            {son.insights.map((icgoru, i) => (
              <article
                key={i}
                data-testid={`ai-muvekkil-icgoru-${i}`}
                className="rounded-lg border border-border-subtle p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={icgoru.priority === "high" ? "warning" : "neutral"}>
                    {AI_CLIENT_INSIGHT_PRIORITY_NAMES[icgoru.priority]}
                  </Badge>
                  <Badge tone="neutral">{AI_CLIENT_INSIGHT_CATEGORY_NAMES[icgoru.category]}</Badge>
                  <span className="text-xs text-fg-muted">{icgoru.subject}</span>
                  {icgoru.linkedContracts > 0 ? (
                    <span className="text-xs text-fg-muted">
                      {icgoru.linkedContracts} bağlı sözleşme
                    </span>
                  ) : null}
                </div>

                <h3 className="mt-2 text-sm font-semibold text-fg-heading">{icgoru.title}</h3>
                <p className="mt-1 text-sm text-fg">{icgoru.rationale}</p>
                {/*
                  ⚠ KESİN İFADE İŞARETİ (CLOS-1088). Cümle silinmiyor ya da düzeltilmiyor —
                  yanına ne olduğu yazılıyor. Sessizce düzeltmek AI'ın ne dediğini
                  kullanıcıdan gizlemek olurdu; reddetmek kullanıcının kredisini bir
                  kelime seçimi yüzünden yakardı. Risk ailesindeki kararla aynı.
                */}
                {icgoru.certaintyFlagged ? (
                  <p className="mt-1 text-xs text-warning" data-testid="ai-muvekkil-kesinlik">
                    {AI_CERTAINTY_WARNING}
                  </p>
                ) : null}

                {/*
                  ⚠ DOĞRULANMAMIŞ MÜKERRER AYRI UYARIYOR. `null` (mükerrer iddiası değil)
                  ile `false` (iddia var, doğrulanmadı) bilerek ayrı: ikisini aynı saysaydık
                  uyarı ilgisiz içgörülere de basılır ve anlamını yitirirdi.
                */}
                {icgoru.duplicateVerified === false ? (
                  <InfoBanner tone="warning" className="mt-2">
                    <span data-testid={`ai-muvekkil-dogrulanmadi-${i}`}>
                      {AI_CLIENT_DUPLICATE_UNVERIFIED_WARNING}
                    </span>
                  </InfoBanner>
                ) : null}
                {icgoru.duplicateVerified === true ? (
                  <p
                    className="mt-2 text-sm text-fg-muted"
                    data-testid={`ai-muvekkil-dogrulandi-${i}`}
                  >
                    Sistemin kimlik/iletişim eşleşmesi de aynı kaydı gösteriyor.
                  </p>
                ) : null}
              </article>
            ))}

            {son.reviewNeeded.length > 0 ? (
              <InfoBanner tone="warning">
                <div data-testid="ai-muvekkil-belirsizlik">
                  <p className="font-medium">Kesin olmayan noktalar</p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {son.reviewNeeded.map((satir, i) => (
                      <li key={i}>{satir}</li>
                    ))}
                  </ul>
                </div>
              </InfoBanner>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
